import { FC, useCallback, useEffect, useState } from "react";
import type { StepDataValue } from "@/data/enquiries/types";
import { useUserJourneyContext } from "../context/UserJourneyContext";
import { dialpadApi, DialpadOpeningHours } from "@/data/dialpad/api";
import { Phone, PhoneOff } from "lucide-react";
import { CallbackRequestedModal } from "./CallbackRequestedModal";
import { Schema } from "@schema-types/types";
import { cn } from "@shadcn/lib/utils";

const POLL_INTERVAL_MS = 60_000;

function hasDialpadFeature(journey: Schema.JourneySchema | null): boolean {
  return (
    journey?.globalFeatures?.some((f) => f.type === "DialpadCallback") ?? false
  );
}

function resolvePhoneFromEnquiry(
  enquiry: { stepData: { stepId?: string; stepDisplayId?: string; data: Record<string, StepDataValue> }[] } | null,
  phoneNumberFieldPath: string
): string | null {
  if (!enquiry?.stepData) return null;
  const parts = phoneNumberFieldPath.split(".", 2);
  if (parts.length === 1) {
    for (const step of enquiry.stepData) {
      const val = step.data?.[parts[0]];
      if (val != null) return String(val);
    }
    return null;
  }
  const stepIdOrDisplayId = parts[0];
  const fieldName = parts[1];
  const stepData = enquiry.stepData.find(
    (s) => s.stepId === stepIdOrDisplayId || s.stepDisplayId === stepIdOrDisplayId
  );
  const value = stepData?.data?.[fieldName];
  return value != null ? String(value) : null;
}

export const CallMeBackButton: FC = () => {
  const { journey, enquiry, journeyVersion } = useUserJourneyContext();
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [availabilityCheckFailed, setAvailabilityCheckFailed] = useState(false);
  const [openingHours, setOpeningHours] = useState<DialpadOpeningHours | undefined>();

  const dialpadFeature = journey?.globalFeatures?.find(
    (f): f is Schema.DialpadCallbackGlobalFeatureSchema => f.type === "DialpadCallback"
  );
  const phoneNumberFieldPath = dialpadFeature?.config?.phoneNumberFieldPath;
  const hasPhone = phoneNumberFieldPath
    ? !!resolvePhoneFromEnquiry(enquiry, phoneNumberFieldPath)
    : false;

  const isClosed = !loading && !available && !availabilityCheckFailed;
  const availabilityError = !loading && availabilityCheckFailed;
  const needsPhone = !hasPhone && available && !loading;

  const checkAvailability = useCallback(async () => {
    if (!hasDialpadFeature(journey)) return;
    setLoading(true);
    try {
      const res = await dialpadApi.getAvailability({
        journeyId: journey?.id,
        journeyVersion: journeyVersion ?? undefined,
      });
      setAvailable(res.available);
      setAvailabilityCheckFailed(res.availabilityCheckFailed);
      setOpeningHours(res.openingHours?.hoursEnabled ? res.openingHours : undefined);
    } catch {
      setAvailable(false);
      setAvailabilityCheckFailed(true);
    } finally {
      setLoading(false);
    }
  }, [journey]);

  useEffect(() => {
    if (!hasDialpadFeature(journey)) return;
    checkAvailability();
    const id = setInterval(checkAvailability, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [journey, checkAvailability]);

  const handleConfirm = async (): Promise<{ success: boolean; position: number }> => {
    if (!enquiry?.id) return { success: false, position: 0 };
    const res = await dialpadApi.requestCallback({ enquiryId: enquiry.id });
    return { success: res.success, position: res.position };
  };

  const handleFabClick = () => {
    if (isClosed || availabilityError) {
      setModalOpen(true);
      return;
    }
    if (needsPhone || loading) {
      setExpanded((prev) => !prev);
      return;
    }
    setModalOpen(true);
  };

  if (!hasDialpadFeature(journey) || !enquiry) return null;

  const canRequest = available && hasPhone && !loading;

  const getLabel = () => {
    if (loading) return "Checking availability...";
    if (availabilityError) return "Unable to check availability";
    if (isClosed) return "Call centre closed";
    if (needsPhone) return "Enter your phone number first";
    return "Request callback";
  };

  const Icon = isClosed || availabilityError ? PhoneOff : Phone;

  return (
    <>
      <div
        className="fixed top-4 right-4 sm:top-auto sm:bottom-5 sm:right-5 z-50 flex justify-end"
        style={{ width: 190 }}
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
      >
        <button
          onClick={handleFabClick}
          className={cn(
            "group flex items-center rounded-full shadow-lg transition-all duration-300 ease-in-out",
            "sm:animate-callback-fab-attention sm:motion-reduce:animate-none",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            isClosed || availabilityError
              ? "bg-muted text-muted-foreground cursor-pointer"
              : "bg-button text-button-foreground",
            canRequest
              ? "hover:bg-button/90 cursor-pointer"
              : !isClosed && !availabilityError ? "opacity-60 cursor-default" : "",
            expanded ? "pl-4 pr-5 h-12 gap-2.5" : "h-12 w-12 justify-center"
          )}
        >
          <Icon className="h-5 w-5 shrink-0" />
          <span
            className={cn(
              "whitespace-nowrap text-sm font-medium overflow-hidden transition-all duration-300 ease-in-out",
              expanded ? "max-w-[220px] opacity-100" : "max-w-0 opacity-0"
            )}
          >
            {getLabel()}
          </span>
        </button>
      </div>
      <CallbackRequestedModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        isClosed={isClosed}
        availabilityError={availabilityError}
        openingHours={openingHours}
      />
    </>
  );
};
